import React, { useState, useEffect } from 'react';
import {
  Check, Crown, Receipt, Clock, Zap, Search,
  Download, ArrowRight, AlertCircle, BadgeCheck,
  Calendar, ChevronRight, Plus, RefreshCw, TrendingUp, TrendingDown
, CreditCard} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { SkeletonBlock, SkeletonCard, SkeletonKPICards } from '../../components/Skeleton';
// Email verification modal for downgrades (no password needed)
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useFlutterwave, closePaymentModal } from 'flutterwave-react-v3';
import CancelModal from '../../components/CancelModal';
import PaymentModal from '../../components/PaymentModal';
import CryptoModal from '../../components/CryptoModal';
import VerificationOverlay from '../../components/VerificationOverlay';
import LoadingDots from '../../components/LoadingDots';
import './Billing.css';
import { useBilling } from '../../context/BillingContext';
import PlanCard from '../../components/PlanCard';

export default function Billing() {
  // ── Billing context (centralized state) ──
  const {
    currentPlan, periodEnd, isPlanExpired, plans, quota,
    subscriptionStatus, pendingPlan, userBillingCycle, userCreatedAt, loading,
    planHistory, receipts, pendingPayments, loadingHistory,
    upgrading, checkoutLocked,
    setCurrentPlan, setSubscriptionStatus, setPendingPlan,
    setUpgrading, setCheckoutLocked,
    loadOverview, loadHistory, refreshAll,
    handleCancelled, handlePaymentComplete, unlockCheckout,
  } = useBilling();

  // ── Local UI state only ──
  const [showProcessingOverlay, setShowProcessingOverlay] = useState(
    localStorage.getItem('processing_payment') === 'true'
  );
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [activeTab, setActiveTab] = useState('overview');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlanForPayment, setSelectedPlanForPayment] = useState(null);
  const [showCryptoModal, setShowCryptoModal] = useState(false);
  const [showDowngradeConfirm, setShowDowngradeConfirm] = useState(false);
  const [downgradeTarget, setDowngradeTarget] = useState(null);
  const [downgrading, setDowngrading] = useState(false);
  const [dpaStatus, setDpaStatus] = useState(null);
  const [showSavedCardModal, setShowSavedCardModal] = useState(false);
  const [savedCardInfo, setSavedCardInfo] = useState(null);
  const [chargingSavedCard, setChargingSavedCard] = useState(false);
  const [savedCard, setSavedCard] = useState(null);
  const [removingCard, setRemovingCard] = useState(false);
  const [selectedCryptoPayment, setSelectedCryptoPayment] = useState(null);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [cardProvider, setCardProvider] = useState('flutterwave'); // 'flutterwave' or 'paystack'
  const [verifyMessage, setVerifyMessage] = useState('');
  const [verifyComplete, setVerifyComplete] = useState(false);
  const [loadingReceipts, setLoadingReceipts] = useState(new Set());
  const [flwConfig, setFlwConfig] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  // loadOverview is now provided by BillingContext

  // fetchUserBillingCycle is now provided by BillingContext



  // loadHistoryTab is now provided by BillingContext via loadHistory()

  // Load history when switching to history tab
  useEffect(() => { if (activeTab === 'history') loadHistory(); }, [activeTab, loadHistory]);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['plans', 'history'].includes(tabParam)) setActiveTab(tabParam);
    // Fetch active card provider
    api.get('/api/billing/active-card-provider').then(res => {
      if (res.data?.provider) setCardProvider(res.data.provider);
    }).catch(() => {});
  }, [searchParams]);

  // Fetch saved card for auto-renewal (runs on mount and when plan/status changes)
  useEffect(() => {
    api.get('/api/billing/flutterwave-subscription').then(res => {
      if (res.data && res.data.active) {
        setSavedCard(res.data);
      } else {
        setSavedCard(null);
      }
    }).catch(() => {});
  }, [currentPlan, subscriptionStatus]);  // Re-fetch after payment changes plan/status

  // Also fetch on initial mount regardless of dependency changes
  useEffect(() => {
    api.get('/api/billing/flutterwave-subscription').then(res => {
      if (res.data && res.data.active) {
        setSavedCard(res.data);
      }
    }).catch(() => {});
  }, []);  // Run once on mount

  // Fetch DPA status for Enterprise users
  useEffect(() => {
    if (currentPlan === 'enterprise') {
      api.get('/api/billing/dpa-status').then(res => setDpaStatus(res.data)).catch(() => {});
    } else {
      setDpaStatus(null);
    }
  }, [currentPlan]);

  useEffect(() => {
    const status = searchParams.get('checkout');
    const planParam = searchParams.get('plan');
    if (status === 'success' && planParam) {
      localStorage.setItem('processing_payment', 'true');
      setShowProcessingOverlay(true);
      (async () => {
        try {
          const d = JSON.parse(localStorage.getItem('checkout_in_progress') || '{}');
          await api.post('/api/billing/finalize-checkout', { plan: planParam, session_id: localStorage.getItem('checkout_session_id') || '', billing_cycle: d?.billing_cycle || billingCycle });
          await api.post('/api/billing/cancel-checkout').catch(() => {});
          localStorage.removeItem('checkout_in_progress'); localStorage.removeItem('checkout_session_id');
          setCheckoutLocked(false); setUpgrading(null);
          // Force refresh auth context so TopHeader/Sidebar update
          try { const me = await api.get('/api/auth/me'); localStorage.setItem('user_plan', me.data.plan); } catch {}
          await refreshAll();
          localStorage.removeItem('processing_payment');
          setShowProcessingOverlay(false);
          toast.success(`Welcome to ${(plans[planParam]?.name) || planParam}! 🎉`, { id: 'checkout-success', duration: 8000 });
        } catch { localStorage.removeItem('processing_payment'); setShowProcessingOverlay(false); toast.error('Failed to finalize.', { id: 'checkout-error' }); }
        navigate('/dashboard/billing', { replace: true });
      })();
    }
    if (status === 'cancelled') {
      localStorage.removeItem('processing_payment');
      setShowProcessingOverlay(false);
      localStorage.removeItem('checkout_in_progress'); localStorage.removeItem('checkout_session_id');
      setCheckoutLocked(false); setUpgrading(null);
      toast.info('Payment cancelled.', { id: 'checkout-cancelled' });
      navigate('/dashboard/billing', { replace: true });
    }
  }, [searchParams]);

  const handleCardCheckout = (planKey, skipSavedCard = false) => {
    // Silently reset any previous attempt
    setFlwConfig(null);
    setUpgrading(null);
    setVerifyingPayment(false);
    setSelectedPlanForPayment(planKey);
    setUpgrading(planKey);
    (async () => {
      try {
        // Check for saved card first (unless user explicitly chose new card)
        if (!skipSavedCard) {
          // For now, saved card check is provider-specific; keep existing logic
          if (cardProvider === 'flutterwave') {
            const cardRes = await api.get('/api/billing/saved-card-info');
            if (cardRes.data && cardRes.data.has_saved_card) {
              setSavedCardInfo(cardRes.data);
              setShowSavedCardModal(true);
              setUpgrading(null);
              setVerifyingPayment(false);
              setFlwConfig(null);
              return;
            }
          }
          // Paystack saved cards not yet implemented; skip check
        }
        
        let userEmail = '';
        let userName = 'Customer';
        try {
          const meRes = await api.get('/api/auth/me');
          userEmail = meRes.data.email || '';
          userName = meRes.data.full_name || meRes.data.email?.split('@')[0] || 'Customer';
        } catch (e) {}
        
        // Use unified card payment endpoint
        const res = await api.post('/api/billing/pay-card', {
          plan: planKey,
          billing_cycle: billingCycle,
        });
        
        const data = res.data;
        if (data.provider === 'paystack' && data.access_code) {
          // Paystack inline checkout with callbacks
          try {
            const PaystackPop = (await import('@paystack/inline-js')).default;
            const popup = new PaystackPop();
            
            popup.resumeTransaction(data.access_code, {
              style: {
                body: {
                  background: '#0d1117',
                  color: '#e6edf3',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                },
                header: {
                  background: '#161b22',
                  color: '#e6edf3',
                },
                card: {
                  background: '#161b22',
                  border: '1px solid #30363d',
                  borderRadius: '8px',
                },
                input: {
                  background: '#0d1117',
                  border: '1px solid #30363d',
                  color: '#e6edf3',
                  borderRadius: '6px',
                },
                button: {
                  background: '#2563eb',
                  color: '#ffffff',
                  borderRadius: '8px',
                  fontWeight: '600',
                },
                footer: {
                  background: '#161b22',
                  color: '#8b949e',
                },
                brand: {
                  color: '#2563eb',
                },
              },
              onSuccess: (response) => {
                // Payment successful — start verification polling
                setVerifyingPayment(true);
                setVerifyComplete(false);
                setVerifyMessage('Verifying your payment...');
                let attempts = 0;
                const ref = data.reference;
                
                const checkPayment = setInterval(async () => {
                  attempts++;
                  try {
                    const v = await api.get('/api/billing/card-status/' + ref);
                    if (v.data.status === 'successful') {
                      clearInterval(checkPayment);
                      setUpgrading(null);
                      setVerifyComplete(true);
                      setVerifyMessage('Payment confirmed! Upgrading your plan...');
                      setTimeout(() => {
                        setVerifyingPayment(false);
                        setVerifyComplete(false);
                        toast.success('Welcome! Plan upgraded successfully.');
                        refreshAll();
                      }, 2000);
                    } else if (v.data.status === 'failed') {
                      clearInterval(checkPayment);
                      setUpgrading(null);
                      setVerifyingPayment(false);
                      toast.error('Payment failed. Please try again.');
                    } else if (attempts >= 36) {
                      clearInterval(checkPayment);
                      setUpgrading(null);
                      setVerifyingPayment(false);
                      toast.info('Payment is taking longer than expected. Check your email for confirmation.');
                    } else {
                      setVerifyMessage('Confirming payment... (' + attempts + '/36)');
                    }
                  } catch (e) {
                    if (attempts >= 12) {
                      clearInterval(checkPayment);
                      setUpgrading(null);
                      setVerifyingPayment(false);
                      toast.error('Unable to verify payment. Please contact support.');
                    }
                  }
                }, 5000);
              },
              onClose: () => {
                setUpgrading(null);
                toast.info('Payment window closed. You can try again.');
              },
              onError: (error) => {
                setUpgrading(null);
                toast.error('Payment failed: ' + (error.message || 'Unknown error'));
              }
            });
          } catch (e) {
            setUpgrading(null);
            toast.error('Paystack checkout failed. Please try again.');
          }
        } else if (data.data?.tx_ref) {
          // Flutterwave (existing logic)
          const planName = (mergedPlans[planKey]?.name || 'Plan');
          const amount = billingCycle === 'yearly' ? mergedPlans[planKey]?.price_yearly : mergedPlans[planKey]?.price_monthly;
          setFlwConfig({
            public_key: data.data.public_key || '',
            tx_ref: data.data.tx_ref,
            ourTxRef: data.data.tx_ref,
            amount: amount,
            currency: 'USD',
            payment_options: 'card',
            payment_plan: data.data.plan_id || undefined,
            customer: { email: data.data.email || userEmail, name: userName },
            customizations: {
              title: 'iLeadX ' + planName + ' Plan',
              description: planName + ' - ' + billingCycle + ' (Auto-renewal)',
            },
            meta: { plan: planKey, billing_cycle: billingCycle },
          });
        } else {
          setUpgrading(null);
          toast.error('Could not initiate payment. Please try again.');
        }
      } catch (err) {
        setUpgrading(null);
        toast.error(err.response?.data?.detail || 'Payment failed. Please try again.');
      }
    })();
  };

  // Keep old handleFlutterwaveCheckout for backward compatibility (used in saved card modal)
  const handleFlutterwaveCheckout = handleCardCheckout;

  // Flutterwave hook,  triggers modal when flwConfig changes
  useEffect(() => {
    let checkPayment = null;
    
    if (flwConfig?.tx_ref && window.FlutterwaveCheckout) {
      window.FlutterwaveCheckout({
        ...flwConfig,
        callback: (response) => {
          const ref = flwConfig?.ourTxRef || flwConfig?.tx_ref;
          closePaymentModal();
          if (response.status === 'successful') {
            setVerifyingPayment(true);
            setVerifyComplete(false);
            setVerifyMessage('Verifying your payment...');
            let attempts = 0;

            const cardLast4 = response.card ? response.card.last4 : '';
            const cardType = response.card ? response.card.type : '';
            const cardExpiry = response.card ? response.card.expiry : '';
            const flwRef = response.flw_ref || '';
            
            checkPayment = setInterval(async () => {
              attempts++;
              try {
                const v = await api.get('/api/billing/flutterwave-status/' + ref + '?last4=' + cardLast4 + '&card_type=' + encodeURIComponent(cardType) + '&card_expiry=' + encodeURIComponent(cardExpiry) + '&flw_ref=' + encodeURIComponent(flwRef));
                if (v.data.status === 'successful') {
                  clearInterval(checkPayment);
                  setFlwConfig(null);
                  setUpgrading(null);
                  setVerifyComplete(true);
                  setVerifyMessage('Payment confirmed! Upgrading your plan...');
                  setTimeout(() => {
                    setVerifyingPayment(false);
                    setVerifyComplete(false);
                    setShowPaymentModal(false);
                    toast.success('Welcome! Plan upgraded successfully.');
                  // If upgraded to Enterprise, prompt for DPA
                  setTimeout(async () => {
                    try {
                      const me = await api.get('/api/auth/me');
                      if (me.data.plan === 'enterprise') {
                        toast.info('Enterprise requires a DPA. Please complete your Data Processing Agreement.', {
                          action: { label: 'Complete DPA', onClick: () => navigate('/dashboard/dpa') },
                          duration: 10000,
                        });
                      }
                    } catch {}
                  }, 1500);
                    refreshAll();
                  }, 2000);
                } else if (v.data.status === 'failed') {
                  clearInterval(checkPayment);
                  setFlwConfig(null);
                  setUpgrading(null);
                  setVerifyingPayment(false);
                  toast.error('Payment failed. Please try again.');
                } else if (attempts >= 15) {
                  clearInterval(checkPayment);
                  setFlwConfig(null);
                  setUpgrading(null);
                  setVerifyingPayment(false);
                  toast.info('Payment is taking longer than expected. Check your email for confirmation.');
                } else {
                  setVerifyMessage('Verifying your payment... (attempt ' + attempts + ')');
                }
              } catch (e) {
                if (attempts >= 6) {
                  clearInterval(checkPayment);
                  setFlwConfig(null);
                  setUpgrading(null);
                  setVerifyingPayment(false);
                  toast.error('Unable to verify payment. Please contact support.');
                }
              }
            }, 3000);
          } else {
            setFlwConfig(null);
            setUpgrading(null);
            toast.error('Payment was cancelled or failed. Please try again.');
          }
        },
        onclose: () => {
          setFlwConfig(null);
          setUpgrading(null);
          toast.info('Payment window closed. You can try again if payment was not completed.');
        },
      });
    }

    // Cleanup: clear interval when component unmounts or flwConfig changes
    return () => {
      if (checkPayment) clearInterval(checkPayment);
    };
  }, [flwConfig]);

  const processUpgrade = async (planKey) => {
    setUpgrading(planKey);
    try {
      const res = await api.post('/api/billing/upgrade', null, { params: { plan_key: planKey, billing_cycle: billingCycle } });
      const data = res.data;
      if (data.pending_plan && data.plan !== planKey) {
        // Scheduled downgrade,  user stays on current plan until period end
        toast.success(data.message || `Your plan will change to ${plans[planKey]?.name || planKey} at the end of your billing period. You're still on ${currentPlanData.name} until then.`, { duration: 8000 });
      } else {
        setCurrentPlan(planKey);
        toast.success(planKey === 'free' ? "You're back on Free!" : `You're now on ${plans[planKey]?.name}!`);
      }
      refreshAll();
    } catch { toast.error('Something went wrong.'); }
    finally { setUpgrading(null); }
  };

  const mergedPlans = plans && Object.keys(plans).length > 0 ? plans : {};
  const currentPlanData = mergedPlans[currentPlan] || { name: 'Free', price_monthly: 0, price_yearly: 0, features: [] };
  const isYearly = userBillingCycle === 'yearly';
  const yearlyMonthlyPrice = currentPlanData.price_yearly ? Math.round(currentPlanData.price_yearly / 12) : 0;
  const planPrice = isYearly ? yearlyMonthlyPrice : currentPlanData.price_monthly;
  const planPriceDisplay = planPrice === 0 ? 'Free' : isYearly ? `$${currentPlanData.price_yearly}/yr` : `$${planPrice}/mo`;

  if (loading) {
    return (
      <div className="page-content" style={{ maxWidth: 'clamp(320px, 100%, 1000px)', margin: '0 auto', padding: '0 clamp(4px, 1vw, var(--space-lg))' }}>
        <div style={{ marginBottom: 'var(--space-xl)' }}>
          <SkeletonBlock width="220px" height={28} style={{ marginBottom: 8 }} />
          <SkeletonBlock width="340px" height={16} />
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 'var(--space-2xl)' }}>
          {[1,2,3].map(i => <SkeletonBlock key={i} width="100px" height={36} style={{ borderRadius: 10 }} />)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 'var(--space-xl)' }}>
          <SkeletonCard lines={6} />
          <SkeletonCard lines={5} />
        </div>
      </div>
    );
  }

  return (
    <div className="billing-page">
      
      {/* ═══════════════ HEADER ═══════════════ */}
      <div className="billing-header">
        <h1><span style={{ fontSize: 28 }}><CreditCard size={18} /></span> Billing & Usage</h1>
        <p>Manage your plan, track usage, and view history.</p>
      </div>

      {/* ═══════════════ STATUS BANNERS ═══════════════ */}
      {subscriptionStatus === 'cancelled' && (
        <div className="billing-banner cancelled">
          <div className="billing-banner-body">
            <Calendar size={18} color="var(--error)" />
            <div className="billing-banner-text">
              <strong style={{ color: 'var(--error)' }}>Subscription Cancelled</strong>,  You have full access until {periodEnd ? new Date(periodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'the end of your billing period'}. After that, your account will become inactive.
            </div>
          </div>
          <button onClick={async () => { try { await api.post('/api/billing/reactivate'); setSubscriptionStatus('active'); toast.success('Plan reactivated!'); refreshAll(); } catch { toast.error('Failed.'); } }} className="auth-button auth-button-primary" style={{ padding: '8px 18px', fontSize: 13 }}><RefreshCw size={18} /> Reactivate</button>
        </div>
      )}

      {pendingPlan && subscriptionStatus !== 'cancelled' && (
        <div className="billing-banner pending">
          <div className="billing-banner-body">
            <ArrowRight size={18} color="var(--info)" />
            <div className="billing-banner-text">
              <strong>Scheduled downgrade:</strong> <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{currentPlanData.name}</span> → <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{mergedPlans[pendingPlan]?.name || pendingPlan}</span>
              <div className="sub-text">
                ⓘ You remain on <strong style={{ color: 'var(--text-primary)' }}>{currentPlanData.name}</strong> with all its features until <strong style={{ color: 'var(--text-primary)' }}>{periodEnd ? new Date(periodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'the end of your billing period'}</strong>.
                After that, your plan will automatically switch to {mergedPlans[pendingPlan]?.name || pendingPlan}.
              </div>
            </div>
          </div>
          <button onClick={async () => { try { await api.post('/api/billing/clear-pending'); setPendingPlan(null); toast.success('Plan change cancelled.'); loadOverview(); } catch { toast.error('Failed.'); } }} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--info)', background: 'transparent', color: 'var(--info)', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>Keep Current</button>
        </div>
      )}

      {/* DPA Banner for Enterprise Users */}
      {currentPlan === 'enterprise' && dpaStatus && dpaStatus.status !== 'fully_executed' && (
        <div className="status-bar-item warning" style={{ marginBottom: 'var(--space-lg)' }}>
          <div className="status-bar-content">
            <span className="status-bar-icon">{dpaStatus.status === 'signed' ? '📝' : '⚠️'}</span>
            <div>
              <strong>{dpaStatus.status === 'signed' ? 'DPA Signed. Awaiting Countersignature' : 'DPA Pending. Complete Your Data Processing Agreement'}</strong>
              <span>{dpaStatus.status === 'signed' ? 'Your DPA has been submitted. We will countersign within 24 hours.' : 'Required to activate all Enterprise features.'}</span>
            </div>
          </div>
          <button onClick={() => navigate('/dashboard/dpa')} className="status-bar-action primary">
            {dpaStatus.status === 'signed' ? 'View DPA' : 'Complete DPA'}
          </button>
        </div>
      )}

      {/* ═══════════════ TABS ═══════════════ */}
      <div className="billing-tabs">
        {[
          { id: 'overview', icon: <Crown size={14} />, label: 'Overview' },
          { id: 'plans', icon: <TrendingUp size={14} />, label: 'Plans' },
          { id: 'history', icon: <Receipt size={14} />, label: 'History' },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`billing-tab ${activeTab === tab.id ? 'active' : ''}`}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* ═══════════════ OVERVIEW TAB ═══════════════ */}
      {activeTab === 'overview' && (
        <>
          <div className="billing-overview-grid">
            {/* Current Plan Card */}
            <div className={`billing-plan-card ${isPlanExpired ? 'expired' : ''}`}>
              <div className="plan-card-top">
                <div>
                  <div className="plan-card-badge">{currentPlan === 'free' ? 'Free Trial' : currentPlanData.name + ' Plan'}</div>
                  {isPlanExpired && <span className="plan-expired-tag">Expired</span>}
                </div>
                <div className="plan-card-price">{planPriceDisplay}</div>
              </div>
              
              <p className="plan-card-cycle">{isYearly ? 'Billed annually' : 'Billed monthly'}{periodEnd && currentPlan !== 'free' ? ' · Renews ' + new Date(periodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</p>

              {/* Period progress */}
              {(() => {
                const now = new Date();
                let end, periodDays, label;
                if (currentPlan === 'free') {
                  const created = userCreatedAt ? new Date(userCreatedAt) : new Date();
                  end = new Date(created); end.setDate(end.getDate() + 7);
                  periodDays = 7; label = 'Trial period';
                } else if (periodEnd) {
                  end = new Date(periodEnd);
                  periodDays = userBillingCycle === 'yearly' ? 365 : 30;
                  label = 'Billing period';
                } else return null;
                if (currentPlan === 'free' && now > end) return null;
                const periodStart = new Date(end); periodStart.setDate(periodStart.getDate() - periodDays);
                const pctUsed = Math.min(100, Math.max(0, Math.round(((now - periodStart) / (end - periodStart)) * 100)));
                return (
                <div className="plan-progress">
                  <div className="plan-progress-header">
                    <span>{label}</span>
                    <span>{currentPlan === 'free' ? 'Trial ends' : 'Renews'} {end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · {pctUsed}%</span>
                  </div>
                  <div className="plan-progress-bar"><div style={{ width: pctUsed + '%' }} /></div>
                </div>);
              })()}

              {/* Features */}
              <div className="plan-features-list">
                {currentPlanData.features?.slice(0, 4).map((f, i) => (
                  <div key={i} className="plan-feature-item"><Check size={14} color="var(--success)" /> {f}</div>
                ))}
              </div>

              <button onClick={() => setActiveTab('plans')} className="auth-button auth-button-primary">Change Plan</button>
              
              {currentPlan === 'enterprise' && dpaStatus && dpaStatus.status === 'fully_executed' && (
                <a href="/dashboard/dpa" style={{ display: 'block', marginTop: 12, color: 'var(--success)', fontSize: 12, fontWeight: 500, textDecoration: 'none', textAlign: 'center' }}>
                  <Check size={12} style={{ marginRight: 4 }} /> View Signed DPA
                </a>
              )}
              
              {currentPlan !== 'free' && subscriptionStatus !== 'cancelled' && (
                <button onClick={() => setShowCancelModal(true)} className="cancel-subtle-link">Cancel Subscription</button>
              )}
            </div>

            {/* Usage Card */}
            {quota && quota.usage && (
              <div className="billing-usage-card">
                <h4>Usage This Period</h4>
                <UsageBar icon={<Search size={15} />} label="Searches" used={quota.usage?.searches || 0} limit={quota.limits?.searches || 0} pct={quota.percentages?.searches || 0} color="var(--accent-primary)" />
                <UsageBar icon={<Zap size={15} />} label="AI Scores" used={quota.usage?.ai_scores || 0} limit={quota.limits?.ai_scores || 0} pct={quota.percentages?.ai_scores || 0} color="var(--accent-secondary)" />
                <UsageBar icon={<Download size={15} />} label="Exports" used={quota.usage?.exports || 0} limit={quota.limits?.exports || 0} pct={quota.percentages?.exports || 0} color="var(--success)" />
                <UsageBar icon={<RefreshCw size={15} />} label="Context Changes" used={quota.usage?.service_context_changes || 0} limit={quota.limits?.service_context_changes || 0} pct={quota.percentages?.service_context_changes || 0} color="var(--info)" />
                {(quota.percentages?.searches >= 80 || quota.percentages?.ai_scores >= 80) && (
                  <div className="usage-warning"><AlertCircle size={15} /> Approaching limits, consider upgrading</div>
                )}
              </div>
            )}

            {/* Payment Method Card */}
            {currentPlan !== 'free' && (
              <div className="billing-payment-method-card">
                <h4>Payment Method</h4>
                {savedCard ? (
                  <>
                    <div className={`bank-card ${(savedCard.card_type || '').toLowerCase().includes('visa') ? 'visa' : 'mastercard'}`}>
                      <div className="bank-card-chip">●●●●</div>
                      <div className="bank-card-number">
                    {(() => {
                      const first = savedCard.card_first6 || savedCard.first6 || '';
                      const last = savedCard.card_last4 || savedCard.last4 || '****';
                      if (first && first.length >= 4) {
                        return first.substring(0, 4) + ' •••• •••• ' + last;
                      }
                      return '•••• •••• •••• ' + last;
                    })()}
                  </div>
                      <div className="bank-card-footer">
                        <div>
                          <div className="bank-card-label">VALID THRU</div>
                          <div className="bank-card-expiry">{savedCard.card_expiry || 'N/A'}</div>
                        </div>
                        <div className="bank-card-brand">
                          {(savedCard.card_type || '').toUpperCase().includes('VISA') ? (
                    <svg width="85" height="30" viewBox="0 0 85 30" fill="none">
                      <rect width="85" height="30" rx="3" fill="#1A1F71"/>
                      <polygon points="4,8 8,5 8,13" fill="white"/>
                      <text x="14" y="23" fill="white" fontSize="24" fontWeight="900" fontFamily="Arial, sans-serif" fontStyle="italic" letterSpacing="-1">VISA</text>
                    </svg>
                  ) : (
                    <svg width="50" height="32" viewBox="0 0 50 32" fill="none">
                      <circle cx="18" cy="16" r="16" fill="#EB001B"/>
                      <circle cx="32" cy="16" r="16" fill="#F79E1B" opacity="0.8"/>
                    </svg>
                  )}
                        </div>
                      </div>
                    </div>
                    <p className="bank-card-status">Your card will be charged automatically on your renewal date. No action needed from you.</p>
                    <button 
                    onClick={() => { if(!removingCard) { setRemovingCard(true); api.post('/api/billing/flutterwave-subscription/cancel').then(() => { setSavedCard(null); setRemovingCard(false); }).catch(() => setRemovingCard(false)); } }}
                    disabled={removingCard}
                    style={{ fontSize: 11, color: 'var(--text-tertiary)', cursor: removingCard ? 'default' : 'pointer', opacity: removingCard ? 0.5 : 1, border: '1px solid var(--border-primary)', borderRadius: 6, padding: '4px 12px', background: 'transparent', fontFamily: 'inherit' }}>
                    {removingCard ? <LoadingDots /> : 'Remove card'}
                  </button>
                  </>
                ) : (
                  <div className="bank-card-empty">
                    <p>No payment method on file. Add a card to enable auto-renewal.</p>
                    <button onClick={() => setActiveTab('plans')} className="auth-button auth-button-primary">Set Up Auto-Renewal</button>
                  </div>
                )}
              </div>
            )}
          </div>


        </>
      )}

      {/* ═══════════════ PLANS TAB ═══════════════ */}
      {activeTab === 'plans' && (
        <>
          <div className="billing-cycle-toggle">
            <div className="billing-cycle-container">
              <div className={`billing-cycle-slider ${billingCycle === 'yearly' ? 'yearly' : ''}`} />
              <button onClick={() => setBillingCycle('monthly')} className={`billing-cycle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}>Monthly</button>
              <button onClick={() => setBillingCycle('yearly')} className={`billing-cycle-btn ${billingCycle === 'yearly' ? 'active' : ''}`}>Yearly</button>
              <span className="billing-cycle-save-badge">Save 17%</span>
            </div>
          </div>

          <div className="billing-plans-grid-new">
            {['starter', 'growth', 'pro', 'enterprise'].map(key => {
              const plan = mergedPlans[key];
              if (!plan) return null;
              const price = billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
              const monthlyEquivalent = billingCycle === 'yearly' ? Math.round(plan.price_yearly / 12) : null;
              const isCurrent = currentPlan === key;
              const planOrder = ['free', 'starter', 'growth', 'pro', 'enterprise'];
              const currentIdx = planOrder.indexOf(currentPlan);
              const targetIdx = planOrder.indexOf(key);
              const isUpgrade = targetIdx > currentIdx;
              const isDowngrade = targetIdx < currentIdx;

              return (
                <div key={key} className={`plan-card-new ${isCurrent ? 'current' : ''} ${plan.highlight ? 'highlight' : ''}`}>
                  {plan.badge && <span className="plan-card-badge-top">{plan.badge}</span>}
                  {isCurrent && !plan.badge && <span className="plan-card-badge-top current-badge">Your Plan</span>}
                  
                  <h3 className="plan-card-name">{plan.name}</h3>
                  <p className="plan-card-tagline">{plan.tagline}</p>
                  
                  <div className="plan-card-price-row">
                    <span className="plan-card-amount">${billingCycle === 'yearly' ? monthlyEquivalent : price}</span>
                    <span className="plan-card-period">/mo</span>
                  </div>
                  {billingCycle === 'yearly' && (
                    <div className="plan-card-yearly">${price}/year · Save {Math.round((1 - price/(plan.price_monthly*12)) * 100)}%</div>
                  )}
                  
                  <ul className="plan-card-features">
                    {plan.features?.slice(0, 5).map((f, i) => (
                      <li key={i}><Check size={14} color="var(--success)" /> {f}</li>
                    ))}
                  </ul>
                  
                  {isCurrent ? (
                    <button className="plan-card-btn current" disabled>Current Plan</button>
                  ) : isUpgrade ? (
                    <button className="plan-card-btn upgrade" 
                      onClick={() => { setSelectedPlanForPayment(key); setShowCryptoModal(true); }}
                      disabled={upgrading === key}
                      style={{ opacity: upgrading === key ? 0.7 : 1, cursor: upgrading === key ? 'wait' : 'pointer' }}>
                      {upgrading === key ? <><LoadingDots /> Processing...</> : <>Upgrade to {plan.name}</>}
                    </button>
                  ) : isDowngrade ? (
                    <button className="plan-card-btn downgrade" onClick={() => { setDowngradeTarget(key); setShowDowngradeConfirm(true); }}>
                      Switch to {plan.name}
                    </button>
                  ) : (
                    <button className="plan-card-btn" onClick={() => { setSelectedPlanForPayment(key); setShowCryptoModal(true); }}>
                      Choose {plan.name}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Feature Comparison Table. collapsible */}
          <div className="feature-compare-section">
            <button className="feature-compare-toggle" onClick={() => {
              const el = document.getElementById('feature-compare-table');
              if (el) {
                const isOpening = !el.classList.contains('open');
                el.classList.toggle('open');
                if (isOpening) {
                  setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
                }
              }
            }}>
              Compare all features <ChevronRight size={14} style={{transition:'transform 0.2s'}} />
            </button>
            <div id="feature-compare-table" className="feature-compare-table-wrap">
              <table className="feature-compare-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Starter</th>
                    <th className="highlight-col">Growth</th>
                    <th>Pro</th>
                    <th>Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="cat-row"><td colSpan="5">Search & Discovery</td></tr>
                  <tr><td>Searches/month</td><td>10</td><td className="highlight-col">100</td><td>500</td><td>1,000</td></tr>
                  <tr><td>Leads per search</td><td>5</td><td className="highlight-col">15</td><td>30</td><td>50</td></tr>
                  <tr><td>Concurrent searches</td><td>1</td><td className="highlight-col">3</td><td>5</td><td>15</td></tr>
                  <tr className="cat-row"><td colSpan="5">AI Scoring</td></tr>
                  <tr><td>Lead scoring</td><td>50 scores</td><td className="highlight-col">Unlimited</td><td>Unlimited</td><td>Unlimited</td></tr>
                  <tr><td>Pitch angles</td><td>1</td><td className="highlight-col">1</td><td>3</td><td>5</td></tr>
                  <tr><td>Pain point detection</td><td>—</td><td className="highlight-col check">✓</td><td className="check">✓</td><td className="check">✓</td></tr>
                  <tr><td>Revenue leakage</td><td>—</td><td>—</td><td className="check">✓</td><td className="check">✓</td></tr>
                  <tr className="cat-row"><td colSpan="5">Integrations</td></tr>
                  <tr><td>CSV export</td><td>Unlimited</td><td className="highlight-col">Unlimited</td><td>Unlimited</td><td>Unlimited</td></tr>
                  <tr><td>Google Sheets</td><td>—</td><td className="highlight-col check">✓</td><td>Auto-sync</td><td>Live sync</td></tr>
                  <tr><td>Zapier + Slack</td><td>—</td><td className="highlight-col check">✓</td><td className="check">✓</td><td className="check">✓</td></tr>
                  <tr><td>HubSpot CRM</td><td>—</td><td>—</td><td className="check">✓</td><td>Full sync</td></tr>
                  <tr><td>API & Webhooks</td><td>—</td><td>—</td><td className="check">✓</td><td className="check">✓</td></tr>
                  <tr className="cat-row"><td colSpan="5">Support</td></tr>
                  <tr><td>Email support</td><td>48hr</td><td className="highlight-col">12hr</td><td>4hr</td><td>1hr</td></tr>
                  <tr><td>Live chat</td><td>✓</td><td className="highlight-col check">Priority</td><td>Top priority</td><td>Dedicated</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ═══════════════ HISTORY TAB ═══════════════ */}
      {activeTab === 'history' && (
        <div className="billing-history-container">
          {/* Plan History Timeline */}
          <div className="billing-section-card">
            <h4 className="section-card-title">Plan History</h4>
            {planHistory.length > 0 ? (
              <div className="history-timeline">
                {planHistory.map((event, i) => (
                  <div key={i} className={`timeline-item ${event.is_current ? 'current' : ''}`}>
                    <div className="timeline-dot" />
                    <div className="timeline-content">
                      <div className="timeline-label">{event.description}</div>
                      <div className="timeline-date">
                        {new Date(event.date).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        {event.is_current && <span className="timeline-current-tag">CURRENT</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : loadingHistory ? <div className="billing-loading"><div className="loader" /></div> : (
              <p className="billing-empty">No plan history yet.</p>
            )}
          </div>

          {/* Pending Crypto Payments */}
          {pendingPayments.length > 0 && (
            <div className="billing-section-card">
              <h4 className="section-card-title">Pending Crypto Payments</h4>
              <div className="billing-table-wrap">
                <table className="billing-table">
                  <thead>
                    <tr>
                      <th>Plan</th><th className="right">Amount</th><th className="center">Status</th><th className="right">Created</th><th className="right">Expires</th><th className="center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingPayments.map(p => (
                      <tr key={p.payment_id}>
                        <td className="capitalize">{p.plan}</td>
                        <td className="right">${p.amount_usd?.toFixed(2)}</td>
                        <td className="center"><span className={`status-pill ${p.payment_status}`}>{p.payment_status}</span></td>
                        <td className="right muted">{new Date(p.created_at).toLocaleDateString()}</td>
                        <td className="right muted">{p.expires_at ? new Date(p.expires_at).toLocaleDateString() : '—'}</td>
                        <td className="center"><button onClick={() => { setSelectedCryptoPayment(p); setShowCryptoModal(true); }} className="table-action-btn">View</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Payment Receipts */}
          <div className="billing-section-card">
            <h4 className="section-card-title">Payment Receipts</h4>
            {receipts.length > 0 ? (
              <div className="billing-table-wrap">
                <table className="billing-table">
                  <thead>
                    <tr>
                      <th>Date</th><th>Plan</th><th className="right">Amount</th><th className="center">Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receipts.map(rec => (
                      <tr key={rec.id}>
                        <td>{new Date(rec.date).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                        <td>{rec.plan?.charAt(0).toUpperCase() + rec.plan?.slice(1)} ({rec.method})</td>
                        <td className="right">${rec.amount?.toFixed(2)}</td>
                        <td className="center">
                          <button onClick={async () => { setLoadingReceipts(prev => new Set([...prev, rec.id])); try { const res = await api.get(rec.download_url, { responseType: 'blob' }); const blob = new Blob([res.data], { type: 'application/pdf' }); const url = window.URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `iLeadX_Receipt_${rec.id}.pdf`; document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url); } catch { toast.error('Failed'); } finally { setLoadingReceipts(prev => { const next = new Set(prev); next.delete(rec.id); return next; }); } }} disabled={loadingReceipts.has(rec.id)} className="receipt-dl-btn">
                            {loadingReceipts.has(rec.id) ? <LoadingDots color="var(--accent-primary)" /> : 'PDF ↓'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="billing-empty">No receipts yet.</p>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════ SAVED CARD MODAL ═══════════════ */}
      {showSavedCardModal && savedCardInfo && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.6)',backdropFilter:'blur(4px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:99999,padding:20}} onClick={() => { setShowSavedCardModal(false); setSavedCardInfo(null); }}>
          <div style={{background:'var(--bg-secondary)',borderRadius:20,padding:'48px 36px',maxWidth:440,width:'100%',border:'1px solid var(--border-primary)',boxShadow:'var(--shadow-xl)',textAlign:'center'}} onClick={e => e.stopPropagation()}>
            <CreditCard size={80} color="var(--text-tertiary)" style={{marginBottom:24}} />
            <h3 style={{color:'var(--text-primary)',fontSize:18,fontWeight:700,margin:'0 0 4px'}}>Pay with Saved Card</h3>
            <p style={{color:'var(--text-secondary)',fontSize:13,margin:'0 0 20px'}}>
              {savedCardInfo.card_type} •••• {savedCardInfo.last4}{savedCardInfo.card_expiry ? ' · Expires ' + savedCardInfo.card_expiry : ''}
            </p>
            <button 
              onClick={async () => {
                setChargingSavedCard(true);
                try {
                  const res = await api.post('/api/billing/upgrade-with-saved-card', {
                    plan: selectedPlanForPayment,
                    billing_cycle: billingCycle,
                  });
                  if (res.data.status === 'success') {
                    setShowSavedCardModal(false);
                    setSavedCardInfo(null);
                    toast.success('Upgraded! Your saved card was charged.');
                    refreshAll();
                  }
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Payment failed. Try another card.');
                } finally {
                  setChargingSavedCard(false);
                }
              }}
              disabled={chargingSavedCard}
              style={{width:'100%',padding:'14px',borderRadius:12,border:'none',background:'var(--accent-gradient)',color:'white',cursor:'pointer',fontSize:15,fontWeight:600,marginBottom:12,fontFamily:'inherit',opacity:chargingSavedCard?0.7:1}}>
              {chargingSavedCard ? <LoadingDots /> : 'Pay with This Card'}
            </button>
            <button 
              onClick={() => {
                setShowSavedCardModal(false);
                setSavedCardInfo(null);
                setUpgrading(selectedPlanForPayment);
                setVerifyingPayment(false);
                setFlwConfig(null);
                setShowSavedCardModal(false);
                handleCardCheckout(selectedPlanForPayment, true);
              }}
              style={{width:'100%',padding:'12px',borderRadius:10,border:'1px solid var(--border-primary)',background:'transparent',color:'var(--text-secondary)',cursor:'pointer',fontSize:13,fontWeight:500,fontFamily:'inherit'}}>
              ＋ Use a Different Card
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════ PAYMENT MODAL ═══════════════ */}
      <PaymentModal
        isOpen={showPaymentModal && !!selectedPlanForPayment}
        onClose={() => setShowPaymentModal(false)}
        plan={mergedPlans[selectedPlanForPayment]}
        planName={mergedPlans[selectedPlanForPayment]?.name}
        billingCycle={billingCycle}
        onCardPayment={() => {
          setShowPaymentModal(false);
          handleCardCheckout(selectedPlanForPayment);
        }}
        onCryptoPayment={() => {
          setShowPaymentModal(false);
          setShowCryptoModal(true);
        }}
      />

      {/* ═══════════════ CRYPTO MODAL ═══════════════ */}
      <CryptoModal
        isOpen={showCryptoModal}
        onClose={() => { setShowCryptoModal(false); setSelectedCryptoPayment(null); }}
        plan={mergedPlans[selectedPlanForPayment || selectedCryptoPayment?.plan]}
        planKey={selectedPlanForPayment || selectedCryptoPayment?.plan}
        planName={mergedPlans[selectedPlanForPayment || selectedCryptoPayment?.plan]?.name || (selectedCryptoPayment?.plan || '').charAt(0).toUpperCase() + (selectedCryptoPayment?.plan || '').slice(1)}
        billingCycle={billingCycle || selectedCryptoPayment?.billing_cycle}
        existingPayment={selectedCryptoPayment ? {
          payment_id: selectedCryptoPayment.payment_id,
          amount_usd: selectedCryptoPayment.amount_usd,
          pay_amount: selectedCryptoPayment.pay_amount,
          pay_currency: selectedCryptoPayment.pay_currency,
          pay_address: selectedCryptoPayment.pay_address,
        } : null}
        onCardPayment={() => {
          // Pass the plan key string to handleFlutterwaveCheckout
          const key = selectedPlanForPayment || (typeof selectedCryptoPayment?.plan === 'string' ? selectedCryptoPayment.plan : null);
          if (key) {
            handleFlutterwaveCheckout(key);
          }
        }}
        onPaymentComplete={() => {
          handlePaymentComplete();
          setSelectedCryptoPayment(null);
        }}
      />

      {/* ═══════════════ DOWNGRADE CONFIRMATION MODAL ═══════════════ */}
      {showDowngradeConfirm && downgradeTarget && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.6)',backdropFilter:'blur(4px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:99999,padding:20}} onClick={() => { setShowDowngradeConfirm(false); setDowngradeTarget(null); }}>
          <div style={{background:'var(--bg-secondary)',borderRadius:20,padding:'40px 36px',maxWidth:440,width:'100%',border:'1px solid var(--border-primary)',boxShadow:'var(--shadow-xl)',textAlign:'center'}} onClick={e => e.stopPropagation()}>
            <TrendingDown size={80} color="var(--warning)" style={{marginBottom:24}} />
            <h3 style={{color:'var(--text-primary)',fontSize:18,fontWeight:700,margin:'0 0 20px'}}>Downgrade to {mergedPlans[downgradeTarget]?.name || downgradeTarget}?</h3>
            <p style={{color:'var(--text-secondary)',fontSize:14,lineHeight:1.6,margin:'0 0 32px'}}>
              Your plan will change on{' '}
              <strong>{periodEnd ? new Date(periodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'your renewal date'}</strong>.
              You keep {currentPlanData.name} features until then.
            </p>
            <div style={{display:'flex',gap:10}}>
              <button onClick={() => { if(!downgrading){ setShowDowngradeConfirm(false); setDowngradeTarget(null); } }} 
                disabled={downgrading}
                style={{flex:1,padding:12,borderRadius:10,border:'1px solid var(--border-primary)',background:'transparent',color:downgrading?'var(--text-tertiary)':'var(--text-secondary)',cursor:downgrading?'default':'pointer',fontSize:13,fontWeight:600,fontFamily:'inherit',transition:'all 0.15s',opacity:downgrading?0.5:1}}
                onMouseEnter={e => { if(!downgrading){ e.target.style.background='var(--bg-tertiary)'; e.target.style.borderColor='var(--text-secondary)'; } }}
                onMouseLeave={e => { if(!downgrading){ e.target.style.background='transparent'; e.target.style.borderColor='var(--border-primary)'; } }}
              >Keep Current Plan</button>
              <button onClick={async () => { 
                  setDowngrading(true);
                  await processUpgrade(downgradeTarget); 
                  setDowngrading(false);
                  setShowDowngradeConfirm(false); 
                  setDowngradeTarget(null); 
                }} 
                disabled={downgrading}
                style={{flex:1,padding:12,borderRadius:10,border:'none',background:downgrading?'var(--warning-bg)':'var(--warning)',color:downgrading?'var(--warning)':'#0F172A',cursor:downgrading?'wait':'pointer',fontSize:13,fontWeight:600,fontFamily:'inherit',transition:'all 0.15s',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}
                onMouseEnter={e => { if(!downgrading){ e.target.style.opacity='0.85'; e.target.style.transform='scale(1.02)'; } }}
                onMouseLeave={e => { if(!downgrading){ e.target.style.opacity='1'; e.target.style.transform='scale(1)'; } }}
              >{downgrading ? <LoadingDots /> : 'Confirm Downgrade'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ CANCEL MODAL ═══════════════ */}
      <CancelModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        currentPlan={currentPlan}
        onCancelled={() => { handleCancelled(); }}
      />

      {/* Verification Overlay */}
      <VerificationOverlay
        isVisible={verifyingPayment}
        message={verifyMessage}
        isComplete={verifyComplete}
      />
    </div>
  );
}


// ═══════════════ SHARED COMPONENTS ═══════════════

function UsageBar({ icon, label, used, limit, pct, color, plan }) {
  const isUnlimited = limit >= 999999;
  const remaining = Math.max(limit - used, 0);
  const isNear = !isUnlimited && pct >= 80, isOver = !isUnlimited && pct >= 100;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
        <span style={{ color: 'var(--text-secondary)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>{icon} {label}</span>
        <span style={{ color: isOver ? 'var(--error)' : isNear ? 'var(--warning)' : 'var(--text-secondary)', fontSize: 12, fontWeight: 600 }}>
          {used} / {isUnlimited ? '∞' : limit.toLocaleString()}
          {!isUnlimited && remaining > 0 && <span style={{ fontWeight: 400, marginLeft: 3, color: 'var(--text-tertiary)' }}>({remaining.toLocaleString()} left)</span>}
        </span>
      </div>
      <div style={{ background: 'var(--border-primary)', borderRadius: 3, height: 6, overflow: 'hidden' }}>
        {isUnlimited ? (
          <div style={{ width: '100%', height: '100%', background: 'linear-gradient(90deg, var(--success), var(--accent-primary), var(--accent-secondary))', borderRadius: 3, opacity: 0.5 }} />
        ) : (
          <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: isOver ? 'var(--error)' : isNear ? 'var(--warning)' : color, borderRadius: 3, transition: 'width 1s ease' }} />
        )}
      </div>
    </div>
  );
}


